import { useEffect } from 'react'
import { useClientContext } from '@client-portal-contexts/ClientContext'
import { isNil } from 'lodash'
import mixpanel from 'mixpanel-browser'

import { Feature, FlagType, useFlags } from '@acre/utils'
import { CaseStatus, Maybe, MortgageReason } from '@acre/graphql'
import { mixPanelGlobals, MixPanelType, useMixPanel as mixPanelHook } from '@acre/design-system'
export type returnType = MixPanelType & {
  appendCaseProperties: (properties: mixPanelGlobals, caseDetails?: caseShape) => mixPanelGlobals
}

type caseShape = {
  id?: string | null
  preference_mortgage_reason?: Maybe<MortgageReason>
  status?: Maybe<CaseStatus>
} | null

// @ts-expect-error - Cypress is a global variable
const shouldDisableMixpanel = window.Cypress || import.meta.env.VITEST || import.meta.env.CI

// This hook allows you to import helper methods for calling Mixpanel
// It wraps the similarly named hook in @acre/components wherein you'll
// find more details of how to use it.
export const useMixPanel = (debug: boolean = false): returnType => {
  const client = useClientContext(true)
  const mpAPI = mixPanelHook(debug)
  const useOathKeeper = sessionStorage.getItem('useOathKeeper') === 'true'

  // Using useFlags here instead of useFlag because
  // this hook might be used in component without FlagProvider
  // displayDashboard3 will be undefined and we don't want to set the super properties in this case
  const flags = useFlags()
  const displayDashboard3 = flags?.[FlagType.Feature]?.[Feature.Dashboard3Cp]
  const dashboard3Enabled = displayDashboard3 ? 'enabled' : 'disabled'

  // append info about the user in a persistent way
  useEffect(() => {
    if (!client || (window as any).Cypress) {
      return
    }

    if (shouldDisableMixpanel) {
      try {
        mixpanel.disable()
      } catch {
        // do nothing
      }
      return
    }

    if (mixpanel.has_opted_out_tracking()) {
      // is this cool in client portal? Everyone is going to have dnt on now...
      mixpanel.opt_in_tracking()
    }
    mixpanel.identify(client.id)
    mixpanel.people.set_once({
      'Client Portal User': true,
    })

    if (!isNil(displayDashboard3) && mixpanel.get_property('dashboard_3_cp') !== dashboard3Enabled) {
      mpAPI.setGlobal('dashboard_3_cp', dashboard3Enabled, true)
    }
  }, [mpAPI, client, displayDashboard3, dashboard3Enabled])

  // This function puts a standard set of properties about a case onto an event's properties
  const appendCaseProperties = (properties: { [key: string]: string | number }, caseDetails?: caseShape | null) => {
    if (!caseDetails) {
      return properties
    }
    return Object.assign(properties, {
      'Case ID': caseDetails.id || '',
      'Case Type': caseDetails.preference_mortgage_reason || '',
      'Case status': caseDetails.status || '',
      'CP new login': useOathKeeper,
    })
  }

  return Object.assign(
    {
      appendCaseProperties,
    },
    mpAPI,
  ) as returnType
}
